import { Brick, registerBrick, Transaction, Query, Predicate } from 'olympe';
import BCP from '../../../common/section_bcp/DM_BCP';
import { executeLambda, getLogger } from '@olympeio/core';
import {
    isNumeric,
    padLeftWithZerosFinalDigits
} from "../../section_bcp/mass_import/utils";
export default class BulkCreateBCPRequest extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {*} bcpRequestData
     * @param {ListDef} errorLog
     * @param {*} bCPWorkflowInitial
     * @param {Map} originalExcelData
     * @param {*} updateBcpRequestData
     * @param {function()} forwardEvent
     * @param {function(*)} setRequestCreationReport
     * @param {function(*)} setErrorReport
     * @param {function(boolean)} setWorkflowCompletionStatus
     */
    async update($, [bcpRequestData, errorLog, bCPWorkflowInitial, originalExcelData, updateBcpRequestData], [forwardEvent, setRequestCreationReport, setErrorReport, setWorkflowCompletionStatus]) {
        const errorLogs = errorLog || [];
        const logger = getLogger('BulkCreateBCPRequest');
        const createdRequestLog = [];
        async function workFlowInit(workflowObject) {
            try {
                let workflowResult = await executeLambda($, bCPWorkflowInitial, [workflowObject]);
                return workflowResult;

            } catch (error) {
                throw new Error(`Workflow execution error: ${error.message}`);
            }

        }

        //to update the modified records of bcp request
        async function updateBCPData() {
            for (const input of updateBcpRequestData) {
                const plant = String(input['Plant ID *']);
                const material = input['Material ID *']

                const queryResult = await Query.instancesOf(BCP)
                    .filter(
                        Predicate.and(
                            Predicate.equals(BCP.materialIdProp, material),
                            Predicate.equals(BCP.plantIdProp, plant)
                        )
                    )
                    .execute();
                const t1 = new Transaction();
                const updateprops = new Map();

                //date conversion for ctag,qa and sqa completion date for update the request
                const dueDateCtagUpdate = input["CTAG/AG Completion Date (dd/mm/yyyy)"] !== undefined ? fixExcelDateShift(input["CTAG/AG Completion Date (dd/mm/yyyy)"]) : ""
                const dueDateQaUpdate = input["QA Completion Date (dd/mm/yyyy)"] !== undefined ? fixExcelDateShift(input["QA Completion Date (dd/mm/yyyy)"]) : ""
                const dueDateSqaUpdate = input["SQA Completion Date (dd/mm/yyyy)"] !== undefined ? fixExcelDateShift(input["SQA Completion Date (dd/mm/yyyy)"]) : ""

                updateprops.set(BCP.priorityProp, input["Priority *"]?.toLowerCase());
                updateprops.set(BCP.targetBCPProp, input["Target BCP *"]);
                updateprops.set(BCP.inputSavingEstimatesProp, isNumeric(input["Input saving est. [CHF] :"]) ? input["Input saving est. [CHF] :"] : null);
                updateprops.set(BCP.ghgReductionProp, input["GHG reduction"]?.toLowerCase());
                updateprops.set(BCP.costSavingsProp, input["Any cost savings?"]?.toLowerCase());
                updateprops.set(BCP.remarkProp, input["Remark"]);
                updateprops.set(BCP.oscarIdProp, input["Oscar ID"]);
                updateprops.set(BCP.spendCategory1Prop, input["Spend Cat L1"]);
                updateprops.set(BCP.maguireEligibilityProp, input["Maguire Y/N"]?.toLowerCase());
                updateprops.set(BCP.specificationProp, input["Specification"]);

                updateprops.set(BCP.requestIdProp, input["Material ID *"]?.concat(input["Plant ID *"]));

                updateprops.set(BCP.businessPartnerProp, input["PS/BP email *"]);
                updateprops.set(BCP.procurementManagerProp, input["Buyer Email *"]);
                updateprops.set(BCP.requestorProp, input["Requestor Email *"]);
                updateprops.set(BCP.technicalApplicationGroupProp, input["Application Group email"]);
                updateprops.set(BCP.qaInFactoryProp, input["Quality in Factory Email"]);
                updateprops.set(BCP.qualityAssuranceProp, input["Supplier Quality Assurance Email"]);


                updateprops.set(BCP.alternativeVendorCodeProp, input["Alternative VS"] ? padLeftWithZerosFinalDigits(input["Alternative VS"], 10) : "");
                updateprops.set(BCP.mainVendorLinkedAltVendorProp, input["MV Linked"]);

                // update ps and vendor details 
                updateprops.set(BCP.procurementSpecialistProp, input["Procurement Specialist Email"]);
                updateprops.set(BCP.vdetNameProp, input["Vendor Name"]);
                updateprops.set(BCP.vdetAddressProp, input["Vendor Address"]);
                updateprops.set(BCP.vdetCountryProp, input["Vendor Country"]);
                updateprops.set(BCP.vdetCityProp, input["Vendor City"]);
                updateprops.set(BCP.vdetContactNumberProp, input["Vendor Contact Number"]);
                updateprops.set(BCP.vdetEmailProp, input["Vendor Email"]);

                // update ctag,qa and sqa completion date
                updateprops.set(BCP.ctagCompletionDueDateProp, dueDateCtagUpdate);
                updateprops.set(BCP.qaInFactoryCompletionDueDateProp, dueDateQaUpdate);
                updateprops.set(BCP.sqaCompletionDueDateProp, dueDateSqaUpdate);

                t1.multiUpdate(queryResult.toArray()[0], updateprops)
                const result1 = t1.execute();
                createdRequestLog.push(...(await result1).getCreatedObjects());
            }
        }

        // get Original data from originalExcelData Map  with  [PlantID+MaterialID] as a key
        const getOriginalRecord = (item) => {
            return originalExcelData.get(`${item["Plant ID*"]}${item["Material ID*"]}`);
        }

        //to create the BCP request 
        async function createBCPData() {
            for (const [index, input] of bcpRequestData.entries()) {
                try {
                    logger.info("***createBCPData :: Index", index, bcpRequestData.length)
                    const t = new Transaction();
                    const newBCPRequest = BCP.create(t);
                    const bcpRequestProps = new Map();

                    //date conversion for ctag,qa and sqa completion date for create bcp request
                    const dueDateCtag = input["CTAG/AG Completion Date (dd/mm/yyyy)"] !== undefined ? fixExcelDateShift(input["CTAG/AG Completion Date (dd/mm/yyyy)"]) : ""
                    const dueDateQa = input["QA Completion Date (dd/mm/yyyy)"] !== undefined ? fixExcelDateShift(input["QA Completion Date (dd/mm/yyyy)"]) : ""
                    const dueDateSqa = input["SQA Completion Date (dd/mm/yyyy)"] !== undefined ? fixExcelDateShift(input["SQA Completion Date (dd/mm/yyyy)"]) : ""

                    bcpRequestProps.set(BCP.businessPartnerProp, input["PS/BP email *"]);
                    bcpRequestProps.set(BCP.procurementManagerProp, input["Buyer Email *"]);

                    bcpRequestProps.set(BCP.technicalApplicationGroupProp, input["Application Group email"]);
                    bcpRequestProps.set(BCP.qaInFactoryProp, input["Quality in Factory Email"]);
                    bcpRequestProps.set(BCP.qualityAssuranceProp, input["Supplier Quality Assurance Email"]);

                    bcpRequestProps.set(BCP.remarkProp, input["Remark"]);
                    bcpRequestProps.set(BCP.materialIdProp, input["Material ID *"]);
                    bcpRequestProps.set(BCP.materialDescriptionProp, input["Material Description"]);
                    bcpRequestProps.set(BCP.plantIdProp, input["Plant ID *"]);
                    bcpRequestProps.set(BCP.plantDescriptionProp, input["Plant Description"]);
                    bcpRequestProps.set(BCP.requestorProp, input["Requestor Email *"]);
                    bcpRequestProps.set(BCP.requestDateProp, new Date());
                    bcpRequestProps.set(BCP.statusProp, "0");
                    bcpRequestProps.set(BCP.bCPStatusProp, "BCP Multi Vendor")

                    bcpRequestProps.set(BCP.requestIdProp, input["Material ID *"]?.concat(input["Plant ID *"]));

                    bcpRequestProps.set(BCP.specificationProp, input["Specification"]);
                    bcpRequestProps.set(BCP.priorityProp, input["Priority *"]?.toLowerCase());
                    bcpRequestProps.set(BCP.targetBCPProp, input["Target BCP *"]);
                    bcpRequestProps.set(BCP.ghgReductionProp, input["GHG reduction"]?.toLowerCase());
                    bcpRequestProps.set(BCP.costSavingsProp, input["Any cost savings?"]?.toLowerCase());
                    bcpRequestProps.set(BCP.inputSavingEstimatesProp, isNumeric(input["Input saving est. [CHF] :"]) ? input["Input saving est. [CHF] :"] : null);
                    bcpRequestProps.set(BCP.maguireEligibilityProp, input["Maguire Y/N"]?.toLowerCase());
                    bcpRequestProps.set(BCP.oscarIdProp, input["Oscar ID"]);
                    bcpRequestProps.set(BCP.spendCategory1Prop, input["Spend Cat L1"]);
                    bcpRequestProps.set(BCP.alternativeVendorCodeProp, input["Alternative VS"] ? padLeftWithZerosFinalDigits(input["Alternative VS"], 10) : "");
                    bcpRequestProps.set(BCP.mainVendorLinkedAltVendorProp, input["MV Linked"]);

                    // create ps and vendor details 
                    bcpRequestProps.set(BCP.procurementSpecialistProp, input["Procurement Specialist Email"]);
                    bcpRequestProps.set(BCP.vdetNameProp, input["Vendor Name"]);
                    bcpRequestProps.set(BCP.vdetAddressProp, input["Vendor Address"]);
                    bcpRequestProps.set(BCP.vdetCountryProp, input["Vendor Country"]);
                    bcpRequestProps.set(BCP.vdetCityProp, input["Vendor City"]);
                    bcpRequestProps.set(BCP.vdetContactNumberProp, input["Vendor Contact Number"]);
                    bcpRequestProps.set(BCP.vdetEmailProp, input["Vendor Email"]);
                    bcpRequestProps.set(BCP.ctagCompletionDueDateProp, dueDateCtag);
                    bcpRequestProps.set(BCP.qaInFactoryCompletionDueDateProp, dueDateQa);
                    bcpRequestProps.set(BCP.sqaCompletionDueDateProp, dueDateSqa);

                    t.multiUpdate(newBCPRequest, bcpRequestProps);
                    const result = await t.execute();
                    try {
                        // Trigger Workflow Initialisation for successfully created BCP request
                        let workflowResult = await workFlowInit(...result.getCreatedObjects());
                        logger.info("workflow Result ,index , bcpRequestData.length ", workflowResult, index, bcpRequestData.length)
                        if (index === bcpRequestData.length - 1 && workflowResult.length > 0) {
                            setWorkflowCompletionStatus(true)
                        } else {
                            setWorkflowCompletionStatus(false)
                        }
                    } catch (workflowError) {
                        logger.error(" workflow creation error")
                        errorLogs.push({ ...getOriginalRecord(input), 'Reason': workflowError.message, 'Step': 'Initialisation of the BCP workflow' });
                    }
                    createdRequestLog.push(...result.getCreatedObjects());

                } catch (error) {
                    errorLogs.push({ ...getOriginalRecord(input), 'Reason': error, 'Step': 'Creation of the BCP request' });
                }
            }
        }
        
        //convert the excel date into correct date put by user in excel 
        function fixExcelDateShift(date) {
            const correctDate = new Date(date);
            if (!(correctDate instanceof Date) || isNaN(correctDate)) {
                throw new Error("Invalid Date object");
            }
            const fixed = new Date(correctDate);
            fixed.setDate(fixed.getDate() + 1);
            return fixed.toISOString();
        }


        //Update the existing record if any modification happen for bcp reques
        logger.info("Length of update and create", updateBcpRequestData?.length, bcpRequestData?.length)
        if (updateBcpRequestData.length > 0) {
            await updateBCPData()
            // This is to make sure if create request count is zero we have to show success message after all update

        }

        // create the new bcp record
        if (bcpRequestData.length == 0) {
            setWorkflowCompletionStatus(true)
        }
        else if (bcpRequestData.length > 0) {
            await createBCPData()
        }

        setRequestCreationReport(createdRequestLog);
        setErrorReport(errorLogs);
        forwardEvent();


    }
}

registerBrick('0196a018371504291695', BulkCreateBCPRequest);
